import type { PermissionFeatures } from "../../SettingsPermissions/types/permissions.types.ts"
import type { FileRights } from "../types/file-item.types.ts"

type Codes = Partial<PermissionFeatures> | undefined

/**
 * What the signed-in member may do on the Files screens, read from their
 * permission codes. Seeing a file is the server's answer; these say which
 * actions to offer on the files it returns.
 *
 * A backend without the Files section answers to the document rights alone,
 * as it always did, so a screen loaded during a deploy offers what that
 * backend still allows.
 */
export const buildFileRights = (
  codes: Codes,
  currentUserId: string
): FileRights => {
  const docs = codes?.projects?.docs
  const features = codes?.files?.features
  const editAllowed = !!docs?.project_doc_edit
  const deleteAllowed = !!docs?.project_doc_delete
  const manageOthers = !!docs?.project_doc_manage_others
  const hasFilesSection = features !== undefined

  return {
    currentUserId,
    editAllowed,
    editForeignAllowed: editAllowed && manageOthers,
    deleteAllowed,
    deleteForeignAllowed: deleteAllowed && manageOthers,
    manageOthersAllowed: manageOthers,
    shareAllowed: hasFilesSection ? !!features?.files_share : manageOthers,
    restoreAllowed: hasFilesSection ? !!features?.files_restore : deleteAllowed,
    permanentDeleteAllowed: hasFilesSection
      ? !!features?.files_permanent_delete
      : deleteAllowed,
    customerVisibilityAllowed: hasFilesSection
      ? !!features?.files_customer_visibility
      : editAllowed,
    requestReviewAllowed: hasFilesSection
      ? !!features?.files_request_review
      : editAllowed
  }
}
